import { useState } from 'react';
import {
  Table,
  Button,
  Tag,
  Typography,
  Modal,
  Form,
  Input,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  useInventoryCounts,
  useCreateInventoryCount,
} from '@hooks/useInventory';
import type {
  InventoryCountItem,
  InventoryCountStatus,
  CreateInventoryCountRequest,
} from '@/types/inventory';

const MD_STATUS_COLOR_MAP: Record<InventoryCountStatus, string> = {
  Draft: 'default',
  InProgress: 'processing',
  Completed: 'success',
  Cancelled: 'error',
};

const MD_STATUS_LABEL_MAP: Record<InventoryCountStatus, string> = {
  Draft: 'Чернетка',
  InProgress: 'В процесі',
  Completed: 'Завершено',
  Cancelled: 'Скасовано',
};

interface InventoryCountsPageProps {
  onViewCount: (mdCountId: string) => void;
}

export default function InventoryCountsPage({ onViewCount }: InventoryCountsPageProps) {
  const [mdPage, setMdPage] = useState(1);
  const [mdPageSize, setMdPageSize] = useState(20);
  const [mdIsModalOpen, setMdIsModalOpen] = useState(false);
  const [mdForm] = Form.useForm<CreateInventoryCountRequest>();

  const { data: mdCountsData, isLoading: mdIsLoading } = useInventoryCounts(mdPage, mdPageSize);
  const mdCreateCount = useCreateInventoryCount();

  const mdHandleCreate = async () => {
    const mdValues = await mdForm.validateFields();
    await mdCreateCount.mutateAsync(mdValues);
    setMdIsModalOpen(false);
    mdForm.resetFields();
  };

  const mdColumns: ColumnsType<InventoryCountItem> = [
    {
      title: 'Номер',
      dataIndex: 'number',
      key: 'number',
      width: 140,
    },
    {
      title: 'Локація',
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 120,
      render: (mdValue: string | null) => mdValue ?? 'Вся',
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (mdValue: InventoryCountStatus) => (
        <Tag color={MD_STATUS_COLOR_MAP[mdValue] ?? 'default'}>
          {MD_STATUS_LABEL_MAP[mdValue] ?? mdValue}
        </Tag>
      ),
    },
    {
      title: 'Розпочато',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 160,
      render: (mdValue: string | null) =>
        mdValue ? dayjs(mdValue).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: 'Завершено',
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 160,
      render: (mdValue: string | null) =>
        mdValue ? dayjs(mdValue).format('DD.MM.YYYY HH:mm') : '—',
    },
    {
      title: 'Всього рядків',
      dataIndex: 'totalLines',
      key: 'totalLines',
      width: 120,
      align: 'right',
    },
    {
      title: 'Підраховано',
      dataIndex: 'countedLines',
      key: 'countedLines',
      width: 120,
      align: 'right',
    },
    {
      title: 'Розбіжності',
      dataIndex: 'discrepancyLines',
      key: 'discrepancyLines',
      width: 120,
      align: 'right',
      render: (mdValue: number) => (
        <span style={{ color: mdValue > 0 ? '#ff4d4f' : undefined }}>
          {mdValue}
        </span>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          Інвентаризації
        </Typography.Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setMdIsModalOpen(true)}
        >
          Нова інвентаризація
        </Button>
      </div>

      <Table<InventoryCountItem>
        columns={mdColumns}
        dataSource={mdCountsData?.items}
        rowKey="id"
        loading={mdIsLoading}
        onRow={(mdRecord) => ({
          onClick: () => onViewCount(mdRecord.id),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: mdPage,
          pageSize: mdPageSize,
          total: mdCountsData?.totalCount,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          onChange: (mdNewPage, mdNewPageSize) => {
            setMdPage(mdNewPage);
            setMdPageSize(mdNewPageSize);
          },
        }}
        size="middle"
      />

      <Modal
        title="Нова інвентаризація"
        open={mdIsModalOpen}
        onOk={mdHandleCreate}
        onCancel={() => {
          setMdIsModalOpen(false);
          mdForm.resetFields();
        }}
        confirmLoading={mdCreateCount.isPending}
        okText="Створити"
        cancelText="Скасувати"
      >
        <Form form={mdForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="locationCode" label="Локація (необов'язково)">
            <Input placeholder="Наприклад: A-01, B-02" maxLength={50} />
          </Form.Item>
          <Form.Item name="notes" label="Примітки">
            <Input.TextArea rows={3} maxLength={1000} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
